export const HANDMADE_EVALUATION_TOOL_IDS = Object.freeze({
  CAPE_V: "67cebba8ce47613f6ee36021",
  CAPE_VR: "67cebba8ce47613f6ee36024",
  OVERALL_SEVERITY: "67cebba8ce47613f6ee36027",
});

const item = (responseState) => Object.freeze({ responseState });

const NUMERIC_DIRECT_COMPARISON_SUMMARY = Object.freeze({
  metricFamily: "numericFiveNumber",
  population: "sameInstrumentDirectEvidence",
  minimumN: 5,
});

const NUMERIC_CONTEXT_SUMMARY = Object.freeze({
  metricFamily: "numericFiveNumber",
  minimumN: 5,
});

const CAPEV_STANDARD_ITEMS = Object.freeze({
  overallSeverity: item("requiredNumeric"),
  roughness: item("requiredNumeric"),
  breathiness: item("requiredNumeric"),
  strain: item("requiredNumeric"),
});

const CAPEV_ADDITIONAL_ITEMS = Object.freeze({
  aphonia: item("conditional"),
  asthenia: item("conditional"),
  diplophonia: item("conditional"),
  falsetto: item("conditional"),
  fry: item("conditional"),
  wetGurgly: item("conditional"),
  otherAdditionalFeature: item("conditional"),
});

const CAPEVR_ADDITIONAL_ITEMS = Object.freeze({
  ...CAPEV_ADDITIONAL_ITEMS,
  hardGlottalAttack: item("conditional"),
});

const CAPEV_INSTABILITY_ITEMS = Object.freeze({
  pitchInstability: item("conditional"),
  tremor: item("conditional"),
});

const CAPEVR_INSTABILITY_ITEMS = Object.freeze({
  aphonicBreak: item("conditional"),
  pitchBreak: item("conditional"),
  pitchInstability: item("conditional"),
  spasm: item("conditional"),
  tremor: item("conditional"),
  otherInstability: item("conditional"),
});

export function normalizeHandmadeEvaluationToolId(value) {
  const candidate = value?._id ?? value;

  if (typeof candidate === "string") return candidate;
  if (!candidate || typeof candidate === "object") return null;

  const normalized = candidate.toString?.();
  return typeof normalized === "string" && normalized !== "[object Object]"
    ? normalized
    : null;
}

const HANDMADE_EVALUATION_TOOLS_BY_ID = Object.freeze({
  [HANDMADE_EVALUATION_TOOL_IDS.CAPE_V]: {
    id: HANDMADE_EVALUATION_TOOL_IDS.CAPE_V,
    key: "capev",
    label: "CAPE-V",
    feedbackPresentationContract: Object.freeze({
      directEvidenceToolId: HANDMADE_EVALUATION_TOOL_IDS.CAPE_V,
      statistics: NUMERIC_DIRECT_COMPARISON_SUMMARY,
      pvqdContextPresentation: Object.freeze({
        itemScope: Object.freeze(["overallSeverity", "roughness", "breathiness", "strain"]),
        headingKey: "feedback_source_pvqd_cape_v",
        helpLabelKey: "feedback_context_pvqd_cape_v_capev_help_label",
        helpBodyKey: "feedback_context_pvqd_cape_v_capev_help_body",
      }),
      sections: Object.freeze({
        standard: CAPEV_STANDARD_ITEMS,
        pitch: Object.freeze({
          normalPitch: item("presence"),
          tooLowPitch: item("conditional"),
          tooHighPitch: item("conditional"),
        }),
        loudness: Object.freeze({
          normalLoudness: item("presence"),
          tooQuietLoudness: item("conditional"),
          tooLoudLoudness: item("conditional"),
        }),
        resonance: Object.freeze({ otherResonance: item("conditional") }),
        nasality: Object.freeze({
          normalNasality: item("presence"),
          hyponasality: item("conditional"),
          hypernasality: item("conditional"),
        }),
        instabilities: CAPEV_INSTABILITY_ITEMS,
        additionalFeatures: CAPEV_ADDITIONAL_ITEMS,
      }),
      supportsComments: true,
      supportsOverallImpression: false,
    }),
  },
  [HANDMADE_EVALUATION_TOOL_IDS.CAPE_VR]: {
    id: HANDMADE_EVALUATION_TOOL_IDS.CAPE_VR,
    key: "capevr",
    label: "CAPE-Vr",
    feedbackPresentationContract: Object.freeze({
      directEvidenceToolId: HANDMADE_EVALUATION_TOOL_IDS.CAPE_VR,
      statistics: NUMERIC_DIRECT_COMPARISON_SUMMARY,
      sections: Object.freeze({
        standard: CAPEV_STANDARD_ITEMS,
        pitch: Object.freeze({
          normalPitch: item("presence"),
          tooLowPitch: item("presence"),
          tooHighPitch: item("presence"),
        }),
        loudness: Object.freeze({
          normalLoudness: item("presence"),
          tooQuietLoudness: item("presence"),
          tooLoudLoudness: item("presence"),
        }),
        resonance: Object.freeze({
          normalResonance: item("presence"),
          frontResonance: item("presence"),
          backResonance: item("presence"),
        }),
        nasality: Object.freeze({
          normalNasality: item("presence"),
          hyponasality: item("presence"),
          hypernasality: item("presence"),
        }),
        inconsistencies: Object.freeze({
          vowelsInconsistency: item("presence"),
          sentencesInconsistency: item("presence"),
          extemporaneousSpeechInconsistency: item("presence"),
        }),
        instabilities: CAPEVR_INSTABILITY_ITEMS,
        additionalFeatures: CAPEVR_ADDITIONAL_ITEMS,
      }),
      supportsComments: true,
      supportsOverallImpression: true,
      pvqdContextPresentation: Object.freeze({
        itemScope: Object.freeze(["overallSeverity", "roughness", "breathiness", "strain"]),
        headingKey: "feedback_context_pvqd_cape_v_capevr_heading",
        helpLabelKey: "feedback_context_pvqd_cape_v_capevr_help_label",
        helpBodyKey: "feedback_context_pvqd_cape_v_capevr_help_body",
        statistics: NUMERIC_CONTEXT_SUMMARY,
      }),
    }),
  },
  [HANDMADE_EVALUATION_TOOL_IDS.OVERALL_SEVERITY]: {
    id: HANDMADE_EVALUATION_TOOL_IDS.OVERALL_SEVERITY,
    key: "overall-severity",
    label: "Overall Severity",
    feedbackPresentationContract: Object.freeze({
      directEvidenceToolId: HANDMADE_EVALUATION_TOOL_IDS.OVERALL_SEVERITY,
      statistics: NUMERIC_DIRECT_COMPARISON_SUMMARY,
      sections: Object.freeze({
        standard: Object.freeze({
          overallSeverity: item("requiredNumeric"),
        }),
      }),
      supportsComments: false,
      supportsOverallImpression: false,
      pvqdContextPresentation: Object.freeze({
        itemScope: Object.freeze(["overallSeverity"]),
        includeLearnerMarker: true,
        headingKey: "feedback_context_pvqd_cape_v_overall_severity_heading",
        helpLabelKey: "feedback_context_pvqd_cape_v_overall_severity_help_label",
        helpBodyKey: "feedback_context_pvqd_cape_v_overall_severity_help_body",
        learnerMarkerKey: "feedback_context_overall_severity_learner_marker",
        statistics: NUMERIC_CONTEXT_SUMMARY,
      }),
    }),
  },
});

export function getHandmadeEvaluationToolMeta(toolId) {
  return HANDMADE_EVALUATION_TOOLS_BY_ID[
    normalizeHandmadeEvaluationToolId(toolId)
  ] || null;
}
